import { useSignIn, useSignUp } from "@clerk/clerk-react";
import { useState } from "react";

const S = {
  page: {
    minHeight: "100vh", background: "#0a0a0f", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", padding: "40px 24px",
    fontFamily: "'DM Serif Display', Georgia, serif", color: "#e8e4dc",
    backgroundImage: "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.15) 0%, transparent 70%)"
  },
  logo: { fontSize: 42, fontStyle: "italic", letterSpacing: "-0.02em", marginBottom: 8 },
  tagline: { fontFamily: "'DM Mono', monospace", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(232,228,220,0.4)", marginBottom: 64 },
  card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "40px", width: "100%", maxWidth: 420, backdropFilter: "blur(12px)" },
  tabs: { display: "flex", gap: 0, marginBottom: 32, background: "rgba(255,255,255,0.04)", borderRadius: 99, padding: 4 },
  tab: (active) => ({ flex: 1, background: active ? "rgba(255,255,255,0.1)" : "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", padding: "8px 0", borderRadius: 99, color: active ? "#fff" : "rgba(232,228,220,0.4)", transition: "all 0.2s" }),
  label: { fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(232,228,220,0.4)", marginBottom: 8, display: "block" },
  input: { width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 16px", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" },
  btn: { width: "100%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", marginTop: 8 },
  error: { fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f87171", marginBottom: 12 },
  features: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 64, maxWidth: 640 },
  feat: { textAlign: "center" },
  featIcon: { fontSize: 24, marginBottom: 8 },
  featTitle: { fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(232,228,220,0.5)" }
};

export default function Landing() {
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState("");
  const { signIn, setActive: setSignInActive } = useSignIn();
  const { signUp, setActive: setSignUpActive } = useSignUp();

  const handle = async () => {
    setError(""); setLoading(true);
    try {
      if (tab === "signin") {
        const res = await signIn.create({ identifier: email, password });
        if (res.status === "complete") {
          await setSignInActive({ session: res.createdSessionId });
        } else if (res.status === "needs_first_factor") {
          // handle additional verification if needed
          setError("Additional verification required. Please check your email.");
        } else {
          await setSignInActive({ session: res.createdSessionId });
        }
      } else {
        await signUp.create({ emailAddress: email, password });
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setVerifying(true);
      }
    } catch (e) {
      setError(e.errors?.[0]?.message || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const verifyCode = async () => {
    setError(""); setLoading(true);
    try {
      const res = await signUp.attemptEmailAddressVerification({ code });
      await setSignUpActive({ session: res.createdSessionId });
    } catch (e) {
      setError(e.errors?.[0]?.message || "Invalid code — please try again");
    }
    setLoading(false);
  };

  return (
    <div style={S.page}>
      <a href="/landing.html" style={{ ...S.logo, textDecoration: "none", color: "#e8e4dc" }}>Zeno Finance</a>
      <div style={S.tagline}>Clarity for freelancers</div>

      <div style={S.card}>
        {!verifying ? (
          <>
            <div style={S.tabs}>
              <button style={S.tab(tab === "signin")} onClick={() => setTab("signin")}>Sign In</button>
              <button style={S.tab(tab === "signup")} onClick={() => setTab("signup")}>Sign Up</button>
            </div>
            {error && <div style={S.error}>{error}</div>}
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
            <label style={S.label}>Password</label>
            <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
            <button style={S.btn} onClick={handle} disabled={loading}>
              {loading ? "Please wait..." : tab === "signin" ? "Sign In →" : "Create Account →"}
            </button>
          </>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📧</div>
              <div style={{ fontSize: 20, marginBottom: 8 }}>Check your email</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(232,228,220,0.4)", lineHeight: 1.6 }}>
                We sent a verification code to<br />
                <span style={{ color: "#a5b4fc" }}>{email}</span>
              </div>
            </div>
            {error && <div style={S.error}>{error}</div>}
            <label style={S.label}>Verification Code</label>
            <input style={{ ...S.input, textAlign: "center", fontSize: 24, letterSpacing: "0.3em" }}
              type="text" value={code} onChange={e => setCode(e.target.value)}
              placeholder="000000" maxLength={6} />
            <button style={S.btn} onClick={verifyCode} disabled={loading}>
              {loading ? "Verifying..." : "Verify & Continue →"}
            </button>
            <button onClick={() => { setVerifying(false); setError(""); }}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(232,228,220,0.3)", marginTop: 12, letterSpacing: "0.1em" }}>
              ← Back
            </button>
          </>
        )}
      </div>

      <div style={S.features}>
        {[["💰", "Income Tracking"], ["📊", "Client Insights"], ["🧾", "Tax Estimates"], ["📈", "Cash Runway"], ["✦", "AI Insights"], ["🔒", "Private & Secure"]].map(([icon, title]) => (
          <div key={title} style={S.feat}>
            <div style={S.featIcon}>{icon}</div>
            <div style={S.featTitle}>{title}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 48, fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(232,228,220,0.25)", letterSpacing: "0.1em" }}>
        $12/month · Cancel anytime · 7-day free trial
      </div>

      {/* FAQ Section */}
      <div style={{ maxWidth: 640, width: "100%", marginTop: 80, paddingBottom: 80 }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(232,228,220,0.3)", textAlign: "center", marginBottom: 40 }}>
          Frequently Asked Questions
        </div>
        {[
          {
            q: "What is the best finance app for freelancers?",
            a: "Zeno Finance is built specifically for freelancers. Unlike QuickBooks or Wave which are designed for businesses with employees and accountants, Zeno focuses on what freelancers actually need: real take-home after tax, client profitability tracking, and cash runway."
          },
          {
            q: "How do freelancers track income and taxes?",
            a: "Zeno Finance automatically calculates your income from all your clients combined, estimates your tax bill in real time based on your tax rate, and shows your real take-home after expenses. No manual entry required."
          },
          {
            q: "How do I know which clients are most profitable?",
            a: "Zeno Finance ranks every client by their real hourly rate. Clients earning above 80% of your top rate are marked Active, 40-79% are Slow, and under 40% are At Risk — so you always know where to focus your energy."
          },
          {
            q: "What is cash runway for freelancers?",
            a: "Cash runway is how many months you can survive on your current savings if income stopped today. Zeno Finance calculates this automatically based on your savings and monthly burn rate, so you always know your financial safety net."
          },
          {
            q: "Is Zeno Finance better than QuickBooks for freelancers?",
            a: "QuickBooks is built for businesses with employees, inventory, and accountants. Zeno Finance is built for solo freelancers who need fast, clear answers about their money — without the complexity or the $30+/month price tag."
          },
          {
            q: "How much does Zeno Finance cost?",
            a: "Zeno Finance is $12/month with a 7-day free trial. No credit card required to start. Cancel anytime."
          }
        ].map(({ q, a }, i) => (
          <FAQItem key={i} q={q} a={a} />
        ))}
      </div>

      {/* JSON-LD FAQ Schema for AI and search engines */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "What is the best finance app for freelancers?", "acceptedAnswer": { "@type": "Answer", "text": "Zeno Finance is built specifically for freelancers. Unlike QuickBooks or Wave which are designed for businesses with employees and accountants, Zeno focuses on what freelancers actually need: real take-home after tax, client profitability tracking, and cash runway." } },
          { "@type": "Question", "name": "How do freelancers track income and taxes?", "acceptedAnswer": { "@type": "Answer", "text": "Zeno Finance automatically calculates your income from all your clients combined, estimates your tax bill in real time based on your tax rate, and shows your real take-home after expenses. No manual entry required." } },
          { "@type": "Question", "name": "How do I know which clients are most profitable?", "acceptedAnswer": { "@type": "Answer", "text": "Zeno Finance ranks every client by their real hourly rate. Clients earning above 80% of your top rate are marked Active, 40-79% are Slow, and under 40% are At Risk — so you always know where to focus your energy." } },
          { "@type": "Question", "name": "What is cash runway for freelancers?", "acceptedAnswer": { "@type": "Answer", "text": "Cash runway is how many months you can survive on your current savings if income stopped today. Zeno Finance calculates this automatically based on your savings and monthly burn rate." } },
          { "@type": "Question", "name": "Is Zeno Finance better than QuickBooks for freelancers?", "acceptedAnswer": { "@type": "Answer", "text": "QuickBooks is built for businesses with employees, inventory, and accountants. Zeno Finance is built for solo freelancers who need fast, clear answers about their money — without the complexity or the $30+/month price tag." } },
          { "@type": "Question", "name": "How much does Zeno Finance cost?", "acceptedAnswer": { "@type": "Answer", "text": "Zeno Finance is $12/month with a 7-day free trial. Cancel anytime." } }
        ]
      })}} />
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "20px 0", cursor: "pointer" }} onClick={() => setOpen(!open)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, fontStyle: "italic", color: "#e8e4dc", lineHeight: 1.4 }}>{q}</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: "rgba(232,228,220,0.3)", flexShrink: 0 }}>{open ? "−" : "+"}</div>
      </div>
      {open && (
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "rgba(232,228,220,0.5)", lineHeight: 1.8, marginTop: 12 }}>
          {a}
        </div>
      )}
    </div>
  );
}
